import { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import {
  AuthProvider,
  ProtectedRoute,
  type AppEntitlementManifest,
} from '@es-tools/shared';

const Login = lazy(() => import('./pages/Login'));
const BrandHome = lazy(() => import('./pages/BrandHome'));
const AppPlaceholder = lazy(() => import('./pages/AppPlaceholder'));
const HolyShipped = lazy(() => import('./pages/HolyShipped'));
const AutomationLibrary = lazy(() => import('./pages/AutomationLibrary'));
const DataPlatformPage = lazy(() => import('./pages/DataPlatform'));

const DEFAULT_ALLOWED_EMAIL_DOMAINS = [
  'hirehoratio.co',
  'mengotomars.com',
  'hollowsocks.com',
  'paradigmcapital.xyz',
];

const parseAllowedDomains = (rawValue?: string): string[] => {
  const source = rawValue ?? DEFAULT_ALLOWED_EMAIL_DOMAINS.join(',');
  return source
    .split(',')
    .map((domain) => domain.trim().toLowerCase())
    .filter((domain) => domain.length > 0);
};

const allowedDomains = parseAllowedDomains(import.meta.env.VITE_ALLOWED_EMAIL_DOMAINS);

const DEV_APP_ENTITLEMENTS: AppEntitlementManifest = {
  groups: [
    'mm-app-all-employees@mengotomars.com',
    'mm-app-data@mengotomars.com',
    'mm-app-platform-admins@mengotomars.com',
    'mm-app-access-admins@mengotomars.com',
  ],
  roles: {
    reviews: 'viewer',
    'creative-analytics': 'viewer',
    'brain-dashboard': 'operator',
    'trybe-intelligence': 'operator',
    'dashboard-viewer': 'admin',
    'data-platform': 'operator',
    'automation-monitor': 'admin',
    'ai-center': 'admin',
  },
  source: 'explicit-local-fixture',
  revision: 'local-report-only',
};

type AppProps = {
  authApiBaseUrl: string;
};

function App({ authApiBaseUrl }: AppProps) {
  const devBypassEnabled = import.meta.env.VITE_DEV_BYPASS_AUTH === 'true';

  return (
    <AuthProvider
      allowDevBypass={devBypassEnabled}
      allowedDomains={allowedDomains}
      authApiBaseUrl={authApiBaseUrl}
      devAppEntitlements={devBypassEnabled ? DEV_APP_ENTITLEMENTS : undefined}
    >
      <Router>
        <Suspense fallback={<div className="route-loading" role="status"><span />Loading workspace…</div>}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <BrandHome />
                </ProtectedRoute>
              }
            />
            <Route
              path="/holy-shipped"
              element={
                <ProtectedRoute>
                  <HolyShipped />
                </ProtectedRoute>
              }
            />
            <Route
              path="/holy-shipped/week/:weekNumber"
              element={
                <ProtectedRoute>
                  <HolyShipped />
                </ProtectedRoute>
              }
            />
            <Route
              path="/automation-library"
              element={
                <ProtectedRoute>
                  <AutomationLibrary />
                </ProtectedRoute>
              }
            />
            <Route
              path="/data-platform"
              element={
                <ProtectedRoute
                  requiredAppId="data-platform"
                  defaultRole="none"
                  enforcement="report_only"
                >
                  <DataPlatformPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/analytics"
              element={
                <ProtectedRoute>
                  <AppPlaceholder appName="Analytics Dashboard" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/inventory"
              element={
                <ProtectedRoute>
                  <AppPlaceholder appName="Inventory Manager" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/campaigns"
              element={
                <ProtectedRoute>
                  <AppPlaceholder appName="Marketing Campaigns" />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </Router>
    </AuthProvider>
  );
}

export default App;
